import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';
import { handleAsyncThunkError } from 'redux/reduxHelpers';

const BASE = '/crm/dashboard/quotations';

const makeThunk = (key, path, message) => createAsyncThunk(
  `dashboardQuotation/${key}`,
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/${path}`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, message));
    }
  }
);

export const fetchQuotationSummary      = makeThunk('fetchSummary',      'get_summary_data',       'Failed to fetch quotation summary');
export const fetchQuotationTrend        = makeThunk('fetchTrend',        'get_trend_data',         'Failed to fetch quotation trend');
export const fetchQuotationConversion   = makeThunk('fetchConversion',   'get_conversion_data',    'Failed to fetch conversion funnel');
export const fetchQuotationStages       = makeThunk('fetchStages',       'get_stages_data',        'Failed to fetch quotation stages');
export const fetchQuotationRegion       = makeThunk('fetchRegion',       'get_region_data',        'Failed to fetch region stats');
export const fetchQuotationSalesUnit    = makeThunk('fetchSalesUnit',    'get_sales_unit_data',    'Failed to fetch sales unit stats');
export const fetchQuotationCountry      = makeThunk('fetchCountry',      'get_country_data',       'Failed to fetch country stats');
export const fetchQuotationProvince     = makeThunk('fetchProvince',     'get_province_data',      'Failed to fetch province stats');
export const fetchQuotationFilterOptions = makeThunk('fetchFilterOptions', 'filter_options',       'Failed to fetch filter options');

export const fetchQuotationDetailed = createAsyncThunk(
  'dashboardQuotation/fetchDetailed',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_detailed_data`, { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch quotation records'));
    }
  }
);

const makeSection = (arrayDefault = false) => ({
  data:    arrayDefault ? [] : null,
  loading: false,
  error:   null
});

const initialState = {
  summary:       makeSection(),
  trend:         makeSection(true),
  conversion:    makeSection(true),
  stages:        makeSection(true),
  region:        makeSection(true),
  salesUnit:     makeSection(true),
  country:       makeSection(true),
  province:      makeSection(true),
  filterOptions: makeSection(),
  detailed: {
    rows:      [],
    totalRows: 0,
    page:      1,
    perPage:   50,
    loading:   false,
    error:     null
  },
  lastUpdated: null
};

const thunkMap = [
  ['summary',       fetchQuotationSummary],
  ['trend',         fetchQuotationTrend],
  ['conversion',    fetchQuotationConversion],
  ['stages',        fetchQuotationStages],
  ['region',        fetchQuotationRegion],
  ['salesUnit',     fetchQuotationSalesUnit],
  ['country',       fetchQuotationCountry],
  ['province',      fetchQuotationProvince],
  ['filterOptions', fetchQuotationFilterOptions],
];

const dashboardQuotationSlice = createSlice({
  name: 'dashboardQuotation',
  initialState,
  reducers: {
    resetQuotationDashboard: () => initialState,
  },
  extraReducers: (builder) => {
    thunkMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[key].loading = true;
          state[key].error   = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key].data    = action.payload;
          state[key].loading = false;
          state.lastUpdated  = new Date().toISOString();
        })
        .addCase(thunk.rejected, (state, action) => {
          state[key].loading = false;
          state[key].error   = action.payload;
        });
    });

    builder
      .addCase(fetchQuotationDetailed.pending, (state) => {
        state.detailed.loading = true;
        state.detailed.error   = null;
      })
      .addCase(fetchQuotationDetailed.fulfilled, (state, action) => {
        state.detailed.rows      = action.payload.rows || [];
        state.detailed.totalRows = action.payload.total_rows || 0;
        state.detailed.page      = action.payload.page || 1;
        state.detailed.perPage   = action.payload.per_page || 50;
        state.detailed.loading   = false;
        state.lastUpdated        = new Date().toISOString();
      })
      .addCase(fetchQuotationDetailed.rejected, (state, action) => {
        state.detailed.loading = false;
        state.detailed.error   = action.payload;
      });
  }
});

export const { resetQuotationDashboard } = dashboardQuotationSlice.actions;
export default dashboardQuotationSlice.reducer;
